import React from 'react';
import { PREMIUM_SIZES } from '../utils/pergolaCatalog';
import { useAwningPricing } from '../hooks/useAwningPricing';

const ProductsSection: React.FC = () => {
  const { pricingByModel: awningPricingByModel, isLoading: awningLoading } = useAwningPricing();

  // Calculate starting prices from database
  const getPergolaStartingPrice = () => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(PREMIUM_SIZES[0].priceCAD);

  const getAwningStartingPrice = (): string => {
    if (awningLoading || !awningPricingByModel) {
      return '$2,200'; // Fallback to hardcoded price
    }

    // Get all pricing from all models
    const allPricing = Object.values(awningPricingByModel).flat();

    if (allPricing.length === 0) {
      return '$2,200'; // Fallback if no pricing data
    }

    const prices = allPricing
      .filter(p => p.total_price > 0)
      .map(p => p.total_price);

    if (prices.length === 0) {
      return '$2,200'; // Fallback if no valid prices
    }

    const lowestPrice = Math.min(...prices);
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(lowestPrice);
  };

  return (
    <section id="products" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-primary mb-4">Our Premium Products</h2>
          <p className="text-xl text-gray-600">Elevate Your Outdoor Living Experience</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Pergolas */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            <img 
              src="images/pergolas/pic1.webp" 
              alt="Luxury pergola with motorized louvers" 
              className="w-full h-64 object-cover"
              loading="lazy"
            />
            <div className="p-8">
              <h3 className="text-2xl font-bold text-primary mb-4">Premium &amp; Premium+ Pergolas</h3>
              <p className="text-gray-600 mb-6">
                Create the perfect outdoor room with our motorized pergolas.
                Built to withstand Canadian weather with premium materials and professional installation.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center">
                  <span className="text-accent mr-2">✓</span>
                  <span>Motorized louvers for weather control</span>
                </div>
                <div className="flex items-center">
                  <span className="text-accent mr-2">✓</span>
                  <span>LED lighting integration</span>
                </div>
                <div className="flex items-center">
                  <span className="text-accent mr-2">✓</span>
                  <span>Wind & snow rated for Ontario</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-2xl font-bold text-primary">Starting at {getPergolaStartingPrice()}</span>
                  <p className="text-sm text-gray-600 mt-1">Standard installation and delivery included; before tax</p>
                </div>
                <a
                  href="/pergolas"
                  className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>

          {/* Awnings */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            <img
              src="images/awnings/garden-folding-arm-awnings629a0.webp"
              alt="Motorized retractable awning"
              className="w-full h-64 object-cover"
              loading="lazy"
            />
            <div className="p-8">
              <h3 className="text-2xl font-bold text-primary mb-4">Awnings</h3>
              <p className="text-gray-600 mb-6">
                Extend your living space with retractable awnings.
                Perfect for patios, decks, and commercial spaces with motorized control.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center">
                  <span className="text-accent mr-2">✓</span>
                  <span>Motorized retraction system</span>
                </div>
                <div className="flex items-center">
                  <span className="text-accent mr-2">✓</span>
                  <span>Weather-resistant fabrics</span>
                </div>
                <div className="flex items-center">
                  <span className="text-accent mr-2">✓</span>
                  <span>Wind sensors for protection</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-2xl font-bold text-primary">Starting at {getAwningStartingPrice()}</span>
                  <p className="text-sm text-gray-600 mt-1">including installation</p>
                </div>
                <a
                  href="awnings.html"
                  className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Quote CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-accent to-green-700 rounded-xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
            <p className="text-xl text-green-100 mb-6">Get your instant quote in under 1 minute</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/pergola-quote"
                className="bg-white text-accent px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block shadow-lg"
              >
                ⚡ Get Instant Pergola Quote
              </a>
              <a
                href="/awning-quote"
                className="bg-white text-accent px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block shadow-lg"
              >
                ⚡ Get Instant Awning Quote
              </a>
            </div>
            <p className="text-green-100 mt-4 text-sm">
              No obligation • Instant pricing • Professional installation included
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
